import { useEffect, useState, useMemo } from 'react';

import { isEqual } from 'lodash';
import { createContainer } from 'unstated-next';

import { BreakoutCreateMode } from './enums/BreakoutCreateMode';
import { useAppMessaging } from '../../hooks/useAppMessaging';
import { type BreakoutRoom, type BreakoutUser } from '../../types/breakoutRoomTypes';
import { DispatchedEvents } from '../../types/dispatchedEvents';

const useBreakoutRooms = () => {
    const [roomState, setRoomState] = useState<BreakoutRoom[]>([]);
    const [unassignedUsers, setUnassignedUsers] = useState<BreakoutUser[]>([]);
    const [isCreator, setIsCreator] = useState(false);
    const [roomsOpen, setRoomsOpen] = useState(false);
    const { sendMessage } = useAppMessaging();
    const [roomCount, setRoomCount] = useState(1);
    const [userCount, setUserCount] = useState(1);
    const [createMode, setCreateMode] = useState(BreakoutCreateMode.InitialSetup);
    const [tempCreateMode, setTempCreateMode] = useState(BreakoutCreateMode.AssignAuto);
    const [allowChangeRooms, setAllowChangeRooms] = useState(false);
    const [eventsBound, setEventsBound] = useState(false);
    const [currentRoom, setCurrentRoom] = useState('');

    const processParticipantState = async (state) => {
        const roomData = state.detail.payload.rooms as BreakoutRoom[];
        setRoomState(roomData);
    };

    const processBreakoutStartedStatus = async (state) => {
        setRoomsOpen(state.detail.payload.started);
    };

    const processCreatorState = async (state) => {
        setRoomsOpen(state.detail.payload.started);

        const roomData = state.detail.payload.rooms as BreakoutRoom[];
        setRoomState((prevRoomData) => {
            if (isEqual(prevRoomData, roomData)) {
                return prevRoomData;
            }
            return roomData;
        });

        const userData = state.detail.payload.unassigned as BreakoutUser[];
        setUnassignedUsers((prevUserData) => {
            if (isEqual(prevUserData, userData)) {
                return prevUserData;
            }
            return userData;
        });

        // rooms have already been created, move create mode to AssignManual
        // to allow the creator to make changes (ie: closed and reopened the form)
        if (roomData.length > 0) {
            const mode = state.detail.payload.createMode as BreakoutCreateMode;

            if (mode !== BreakoutCreateMode.InitialSetup) {
                setCreateMode((prevMode) => {
                    if (prevMode === mode) {
                        return prevMode;
                    }
                    return mode;
                });
            }

            const allowChange = state.detail.payload.allowUserChoice as boolean;
            setAllowChangeRooms(allowChange);
        }
    };

    const urlSearchParams = useMemo(() => new URLSearchParams(window.location.search), []);

    const roomCreator = useMemo(
        () => urlSearchParams.get('isCreator')?.toLowerCase() === 'true' ?? false,
        [urlSearchParams]
    );

    const roomsStarted = useMemo(
        () => urlSearchParams.get('roomsOpen')?.toLowerCase() === 'true' ?? false,
        [urlSearchParams]
    );

    const users = useMemo(() => parseInt(urlSearchParams.get('userCount') ?? '1', 10), [urlSearchParams]);

    const room = useMemo(() => urlSearchParams.get('currentRoom') ?? '', [urlSearchParams]);

    useEffect(() => {
        setIsCreator(roomCreator);
    }, [roomCreator]);

    useEffect(() => {
        setRoomsOpen(roomsStarted);
    }, [roomsStarted]);

    useEffect(() => {
        setUserCount(users);
    }, [users]);

    useEffect(() => {
        setCurrentRoom(room);
    }, [room]);

    useEffect(() => {
        if (!eventsBound) return;

        if (!isCreator) {
            sendMessage({
                payload: { func: 'request_active_breakout_users' }
            });
        } else {
            sendMessage({
                payload: { func: 'send_breakout_state' }
            });
        }
    }, [isCreator, sendMessage, eventsBound]);

    useEffect(() => {
        window.addEventListener(DispatchedEvents.ReceivedParticipantBreakoutState, processParticipantState);
        window.addEventListener(DispatchedEvents.ReceivedCreatorBreakoutState, processCreatorState);
        window.addEventListener(DispatchedEvents.ReceivedBreakoutRoomStartedStatus, processBreakoutStartedStatus);

        setEventsBound(true);

        // clean up
        return () => {
            window.removeEventListener(DispatchedEvents.ReceivedParticipantBreakoutState, processParticipantState);
            window.removeEventListener(DispatchedEvents.ReceivedCreatorBreakoutState, processCreatorState);
            window.removeEventListener(
                DispatchedEvents.ReceivedBreakoutRoomStartedStatus,
                processBreakoutStartedStatus
            );
        };
    }, []);

    const getUsersPerRoom = () => {
        const usersPerRoom = userCount / roomCount;
        const remainder = userCount % roomCount;

        // even number of users/room
        if (remainder === 0) {
            return usersPerRoom;
        }

        const rangeBottom = Math.floor(usersPerRoom).toString();
        const rangeTop = Math.floor(usersPerRoom + 1).toString();
        return rangeBottom.concat(' - ', rangeTop);
    };

    const changeRoomCount = (newCount) => {
        if (newCount.target.value > 0 && newCount.target.value <= 50) {
            setRoomCount(newCount.target.value);
        }
    };

    const changeTempCreateMode = (newMode) => {
        setTempCreateMode(newMode);
    };

    const handleCreate = () => {
        setCreateMode(tempCreateMode);

        const payload = {
            func: 'create_breakout_rooms',
            count: Number(roomCount),
            autoAssign: tempCreateMode === BreakoutCreateMode.AssignAuto,
            allowChangeRooms,
            createMode: tempCreateMode
        };

        sendMessage({ payload });
    };

    const handleOpenRooms = () => {
        sendMessage({
            payload: { func: 'open_breakout_rooms' }
        });
    };

    const handleCloseRooms = () => {
        sendMessage({
            payload: { func: 'close_breakout_rooms' }
        });
    };

    const joinBreakout = async (roomId: string) => {
        sendMessage({
            payload: { func: 'participant_selected_breakout_room', room_id: roomId }
        });
    };

    const leaveBreakout = () => {
        sendMessage({
            payload: { func: 'leave_breakout_room' }
        });
    };

    const handleRecreateRooms = () => {
        sendMessage({
            payload: { func: 'remove_all_breakout_rooms' }
        });

        setCreateMode(BreakoutCreateMode.InitialSetup);
    };

    const handleCancel = () => {
        if (createMode !== BreakoutCreateMode.InitialSetup) {
            handleRecreateRooms();
        }

        sendMessage({
            payload: { func: 'cancel_breakout_room_creation' }
        });
    };

    const handleAddRoom = () => {
        sendMessage({
            payload: { func: 'add_breakout_room' }
        });
    };

    return {
        isCreator,
        joinBreakout,
        roomState,
        unassignedUsers,
        roomsOpen,
        handleCreate,
        handleCancel,
        changeRoomCount,
        getUsersPerRoom,
        createMode,
        roomCount,
        tempCreateMode,
        changeTempCreateMode,
        handleOpenRooms,
        handleCloseRooms,
        handleRecreateRooms,
        handleAddRoom,
        allowChangeRooms,
        setAllowChangeRooms,
        currentRoom,
        leaveBreakout
    };
};

const BreakoutRoomOptionsContainer = createContainer(useBreakoutRooms);

export default BreakoutRoomOptionsContainer;
